import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, Button } from "@activecollab/components";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import { keyCodes } from "~/utils/key_codes.jsx";
import { ProjectMembersAssigneePicker } from "../ProjectMembersAssigneePicker.jsx";

const SubtaskRecurringTaskForm = ({
  projectId,
  fakeAssigneeName,
  onSubmit,
  handleCancel,
  assigneeId,
  name,
  id,
  submitText,
  hideFromClients,
}) => {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    assigneeId,
    name,
  });

  const handleSubmit = useCallback(() => {
    id !== undefined && typeof handleCancel === "function" && handleCancel();
    onSubmit({
      id: v4(),
      body: form.name,
      assignee_id: form.assigneeId ? form.assigneeId : null,
    });
  }, [form.assigneeId, form.name, handleCancel, id, onSubmit]);

  const handleInputKeyUp = useCallback(
    (e) => {
      if (e && e.which === keyCodes.escape) {
        e.stopPropagation();
        typeof handleCancel === "function" && handleCancel();
      }
    },
    [handleCancel]
  );

  const handleOnKeyDown = useCallback(
    (e) => {
      if (e.code === "Enter") {
        return handleSubmit();
      }

      if (e.code === "Escape") {
        e.stopPropagation();
        handleCancel();
      }
    },
    [handleCancel, handleSubmit]
  );

  const handleInputChange = useCallback((e) => {
    if (e) {
      const name = e.target.value;
      setForm((prevState) => ({ ...prevState, name }));
    }
  }, []);

  const handleAssigneeChange = useCallback((id) => {
    setForm((prevState) => ({ ...prevState, assigneeId: id }));
  }, []);

  useEffect(() => {
    if (hideFromClients) {
      setForm((prevState) => ({ ...prevState, assigneeId: null }));
    }
  }, [hideFromClients]);

  return (
    <div className="tw-flex-col tw-p-2 tw-bg-theme-200 tw-rounded-lg">
      <div className="tw-flex tw-items-center">
        <Input
          onKeyDown={handleOnKeyDown}
          type="text"
          name="name"
          onKeyUp={handleInputKeyUp}
          placeholder={t("Name of the subtask")}
          value={form.name}
          onChange={handleInputChange}
          required
          autoComplete="off"
          autoFocus
          className="tw-h-10 tw-mr-2"
        />
        <ProjectMembersAssigneePicker
          projectId={projectId}
          assigneeId={form.assigneeId}
          fakeAssigneeName={fakeAssigneeName}
          onChange={handleAssigneeChange}
          hiddenFromClient={hideFromClients}
        />
      </div>
      <div className="tw-flex tw-items-center tw-mt-2">
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={!form.name}
        >
          {submitText ? submitText : t("Add")}
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="tw-ml-2"
          onClick={handleCancel}
        >
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
};

SubtaskRecurringTaskForm.propTypes = {
  onSubmit: PropTypes.func,
  handleCancel: PropTypes.func,
  assigneeId: PropTypes.number,
  name: PropTypes.string,
  fakeAssigneeName: PropTypes.string,
  projectId: PropTypes.number,
  submitText: PropTypes.string,
  id: PropTypes.number,
  hideFromClients: PropTypes.bool,
};

SubtaskRecurringTaskForm.defaultProps = {
  onSubmit: () => null,
  handleCancel: () => null,
  assigneeId: 0,
  name: "",
  fakeAssigneeName: "",
};

SubtaskRecurringTaskForm.displayName = "SubtaskRecurringTaskForm";

export default SubtaskRecurringTaskForm;
